import React, {useEffect, useState} from 'react';
import {actionApiCall} from '@mc/react-app-v4';
import {showConfirm, toaster} from '@mc/ui-v4';
import {EnumFeatureBatchOutcome, FeatureWrapperVO, UserLayersApi} from '../api';
import {OVERLAY_LAYER_ID} from '../const';
import {simplifyProperties} from './DataMutators';

/** Od kolika najednou přesouvaných objektů se uživatele nejdřív zeptáme na potvrzení. */
const MOVE_CONFIRM_THRESHOLD = 50;

/**
 * Jak dlouho se počká, než se server poprvé zeptáme na průběh. Běžné uložení je do té doby hotové,
 * takže na průběh nepadne ani jeden požadavek navíc.
 */
const PROGRESS_START_DELAY_MS = 700;

/** Po jak dlouhé pauze zkusit dotaz na průběh znovu, když selhal – ať z něj nevznikne smyčka požadavků. */
const PROGRESS_RETRY_DELAY_MS = 3000;

/**
 * Dokdy nejpozději musí dotaz na průběh odpovědět. Server drží odpověď nanejvýš deset vteřin, takže delší
 * ticho znamená, že se spojení někde po cestě ztratilo – prohlížeč to sám nepozná a čekal by donekonečna.
 */
const PROGRESS_REQUEST_TIMEOUT_MS = 30000;

export interface FeatureUpdateResult {
    /** Počet featur, které se ukládaly (bez overlay featur, které se na server neposílají). */
    total: number;
    succeeded: number;
    failed: number;
    /** Featury, jejichž uložení selhalo – pro optimistický rollback (jen v režimu continueOnError). */
    failedFeatures: any[];
}

/** Průběh ukládání: kolik featur už server zpracoval z celkového počtu. */
export interface SaveProgress {
    /** Počet zpracovaných featur; `null`, když se průběh nedaří zjistit – pak se místo čísel hlásí jen „probíhá“. */
    done: number | null;
    total: number;
}

const isPersistableFeature = (feature: any): boolean => feature.properties?.layerId !== OVERLAY_LAYER_ID;

const toWrapper = (feature: any): FeatureWrapperVO => ({feature: {...feature, properties: simplifyProperties(feature.properties)}});

/** Seskupí featury podle vrstvy (layerId) – dávkový endpoint je per-vrstva. */
function groupByLayer(features: any[]): {layerId: number; features: any[]}[] {
    const byLayer = new Map<number, any[]>();
    for (const feature of features) {
        const layerId = feature.properties.layerId;
        const bucket = byLayer.get(layerId);
        if (bucket) {
            bucket.push(feature);
        } else {
            byLayer.set(layerId, [feature]);
        }
    }
    return [...byLayer].map(([layerId, layerFeatures]) => ({layerId, features: layerFeatures}));
}

const newProgressId = (): string => globalThis.crypto?.randomUUID?.() ?? `move-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/** Kolikrát po sobě smí server odpovědět, že o dávce nic neví, než se ho na ni přestaneme ptát. */
const MAX_UNKNOWN_PROGRESS_ANSWERS = 10;

/** Jak dávka podle serveru dopadla. */
type BatchOutcome = 'SUCCESS' | 'ERROR';

interface BatchTracker {
    /** Výsledek dávky podle serveru. Nesplní se, pokud server o dávce nic neví – pak zbývá jen odpověď požadavku. */
    outcome: Promise<BatchOutcome>;
    /** Ukončí sledování. */
    stop: () => void;
}

/**
 * Sleduje dávku, kterou server zpracovává: kolik featur už má hotovo a jak nakonec dopadla
 * (viz `FeatureBatchProgressService`).
 *
 * Dotaz je long-polling: server odpoví, až se průběh znatelně posune nebo dávka skončí, a klient se
 * hned ptá znovu. Nejde tedy o dotazování v pevném rytmu – požadavek odejde jen tehdy, když má co
 * přinést, a rychlé uložení (do {@link PROGRESS_START_DELAY_MS}) nestojí ani jeden.
 *
 * Výsledek dávky hlásí server až po commitu, takže je to plnohodnotné potvrzení uložení – slouží jako
 * druhá cesta k dokončení pro případ, že odpověď na samotný požadavek nedorazí (viz {@link awaitBatch}).
 *
 * `onProgress` dostane `null`, když server o dávce nic neví (vypršelý záznam, restart, jiný uzel):
 * ukazovat dál poslední známé číslo by tvrdilo, že ukládání stojí právě na něm.
 */
function trackBatch(progressId: string, onProgress: (done: number | null) => void): BatchTracker {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    let unknownAnswers = 0;
    let reportOutcome: (outcome: BatchOutcome) => void = () => undefined;
    const outcome = new Promise<BatchOutcome>(resolve => {
        reportOutcome = resolve;
    });
    const askLater = (knownDone: number): void => {
        timer = setTimeout(() => void ask(knownDone), PROGRESS_RETRY_DELAY_MS);
    };
    const ask = async (knownDone: number): Promise<void> => {
        if (stopped) {
            return;
        }
        try {
            const {data} = await UserLayersApi.getFeatureBatchProgress(progressId, knownDone, {timeout: PROGRESS_REQUEST_TIMEOUT_MS});
            if (stopped) {
                return;
            }
            if (data.outcome === EnumFeatureBatchOutcome.SUCCESS || data.outcome === EnumFeatureBatchOutcome.ERROR) {
                // Dávka je uzavřená – ukážeme rovnou plný počet, ať průběh nezůstane stát na posledním hlášení.
                onProgress(data.total ?? knownDone);
                reportOutcome(data.outcome === EnumFeatureBatchOutcome.SUCCESS ? 'SUCCESS' : 'ERROR');
                return;
            }
            if (data.outcome === EnumFeatureBatchOutcome.UNKNOWN) {
                unknownAnswers += 1;
                onProgress(null);
                if (unknownAnswers < MAX_UNKNOWN_PROGRESS_ANSWERS) {
                    askLater(knownDone);
                }
                return;
            }
            unknownAnswers = 0;
            const done = data.done ?? 0;
            if (done > knownDone) {
                onProgress(done);
            }
            if (data.outcome == null && data.finished) {
                // Starší server bez výsledku dávky umí říct jen „už neběží“ – dál se ptát nemá smysl.
                return;
            }
            void ask(Math.max(done, knownDone));
        } catch {
            // Průběh je jen doplňková informace – jeho výpadek nesmí ovlivnit samotné ukládání.
            askLater(knownDone);
        }
    };
    timer = setTimeout(() => void ask(0), PROGRESS_START_DELAY_MS);
    return {
        outcome,
        stop: () => {
            stopped = true;
            clearTimeout(timer);
        },
    };
}

/**
 * Počká, až bude dávka vyřízená – ať už odpovědí na samotný požadavek, nebo výsledkem, který server
 * ohlásí přes sledování průběhu ({@link trackBatch}).
 *
 * Ta druhá cesta je záchranná: odpověď na požadavek, který na serveru běží i desítky minut, se cestou
 * ztratit může (proxy, uspaný prohlížeč) a čekání na ni by pak neskončilo nikdy – uživateli by nad mapou
 * zůstala viset hláška o probíhajícím ukládání i dlouho po tom, co se data uložila. Server hlásí výsledek
 * až po commitu, takže je stejně závazný jako odpověď požadavku.
 */
async function awaitBatch(request: Promise<unknown>, tracker: BatchTracker): Promise<void> {
    let requestError: unknown;
    const requestOutcome = request.then<BatchOutcome, BatchOutcome>(
        () => 'SUCCESS',
        error => {
            requestError = error;
            return 'ERROR';
        },
    );
    if ((await Promise.race([requestOutcome, tracker.outcome])) === 'ERROR') {
        // Chybu z požadavku máme jen tehdy, když dorazil právě on; jinak zbývá výsledek hlášený serverem.
        throw requestError ?? new Error('Hromadnou editaci se nepodařilo uložit.');
    }
}

/** Identita featury – podle ní se pozná, že se tentýž objekt mezitím přesunul znovu. */
const featureKey = (feature: any): any => feature.id;

/**
 * Otisk featury pro odložený zápis. Zásadní pro čekající zápisy: engine si své objekty při
 * znovunačtení vrstvy přepisuje (a to dělá po každé změně, o které se dozví přes WebSocket), takže
 * bez vlastní kopie by se zápis čekající ve frontě odeslal s geometrií, kterou mezitím přepsalo
 * znovunačtení – tedy s tou starou. Kopie zároveň chrání data enginu před `simplifyProperties`,
 * které předaný objekt `properties` mění na místě.
 */
const snapshotFeature = (feature: any): any => ({
    ...feature,
    geometry: feature.geometry ? JSON.parse(JSON.stringify(feature.geometry)) : feature.geometry,
    properties: {...feature.properties},
});

interface QueuedWrite {
    features: any[];
    /** Featury, které převzal novější čekající zápis – jejich starší pozici už nemá smysl posílat. */
    superseded: Set<any>;
}

interface LayerWriteQueue {
    /** Zápisy čekající na odeslání, v pořadí zařazení (ten právě odesílaný už v seznamu není). */
    queued: QueuedWrite[];
    /** Řetěz odesílání – na vrstvu jde vždy jen jeden požadavek. */
    chain: Promise<unknown>;
    /** Kolik zápisů vrstvy je rozdělaných; při nule se fronta zahodí. */
    active: number;
}

const layerWriteQueues = new Map<number, LayerWriteQueue>();

/**
 * Zařadí zápis featur vrstvy do fronty a odešle ho, až na něj přijde řada.
 *
 * Řeší souběh dvou přesunů téhož objektu: bez fronty by druhý (malý, rychlý) požadavek mohl
 * doběhnout dřív než první (velká dávka) a ta by pak objekt přepsala zpět na starou pozici – v mapě
 * by byla nová, v databázi stará. Fronta proto zaručuje, že na vrstvu jde vždy jen jeden požadavek
 * a v pořadí, v jakém uživatel objekty přesouval. Navíc platí, že přesune-li se objekt znovu ještě
 * předtím, než jeho starší zápis odejde, starší pozice se z odesílané dávky vypustí – zastaralý
 * zápis tedy vůbec neproběhne. Uloží se vždy poslední pozice, na kterou uživatel objekt přesunul.
 *
 * `send` dostane featury, které mají po vyřazení přepsaných opravdu odejít; při prázdném seznamu se
 * nevolá vůbec (celý zápis nahradil novější).
 */
async function enqueueLayerWrite(layerId: number, features: any[], send: (payload: any[]) => Promise<void>): Promise<void> {
    const queue = layerWriteQueues.get(layerId) ?? {queued: [], chain: Promise.resolve(), active: 0};
    layerWriteQueues.set(layerId, queue);

    const write: QueuedWrite = {features: features.map(snapshotFeature), superseded: new Set()};
    const keys = new Set(write.features.map(featureKey));
    for (const earlier of queue.queued) {
        for (const earlierFeature of earlier.features) {
            if (keys.has(featureKey(earlierFeature))) {
                earlier.superseded.add(featureKey(earlierFeature));
            }
        }
    }
    queue.queued.push(write);
    queue.active += 1;

    const run = queue.chain.then(() => {
        // Od chvíle odeslání už zápis přepsat nejde, proto z čekajících mizí až tady.
        queue.queued.splice(queue.queued.indexOf(write), 1);
        const payload = write.superseded.size > 0 ? write.features.filter(feature => !write.superseded.has(featureKey(feature))) : write.features;
        return payload.length > 0 ? send(payload) : undefined;
    });
    // Chyba jednoho zápisu nesmí zaseknout frontu vrstvy – další zápisy pokračují.
    queue.chain = run.catch(() => undefined);
    try {
        await run;
    } finally {
        queue.active -= 1;
        if (queue.active === 0 && layerWriteQueues.get(layerId) === queue) {
            layerWriteQueues.delete(layerId);
        }
    }
}

/**
 * Uloží zadané featury na server: jeden dávkový požadavek na vrstvu se VŠEMI jejími featurami
 * ({@link UserLayersApi.updateLayerFeaturesBatch}), který server zpracuje v jedné transakci – přesun
 * tedy proběhne najednou, buď celý, nebo vůbec. Nahrazuje dřívější `forEach(async …)`, který posílal
 * jeden HTTP požadavek (a čekací dialog) na každou featuru → při přesunu stovek objektů zamrzla aplikace.
 *
 * Zápisy jdou přes frontu vrstvy ({@link enqueueLayerWrite}), takže se souběžné editace nemohou
 * předběhnout a přepsat novější pozici starší.
 *
 * @param continueOnError `true` → chyby se agregují do výsledku (`failedFeatures`) a ukládání
 *   pokračuje dalšími vrstvami; obchází `actionApiCall`, aby při chybě nevyskočil error modal
 *   (volající si chybu ošetří sám – pro běh na pozadí s rollbackem). `false` (výchozí) → chyby jdou
 *   přes `actionApiCall` (standardní error modal) a první je vyhozena, jako u čekacího dialogu
 *   (kvůli undo/redo, které na chybu spoléhá).
 * @param onProgress dostává průběh hlášený serverem během zpracování dávky. Je to informace
 *   o zpracování, ne o zápisu – ten potvrzuje až doběhnutí celé metody.
 */
export async function updateFeaturesBatched(
    features: any[],
    options: {continueOnError?: boolean; onProgress?: (progress: SaveProgress) => void} = {},
): Promise<FeatureUpdateResult> {
    const {continueOnError = false, onProgress} = options;
    const toSave = features.filter(isPersistableFeature);
    const result: FeatureUpdateResult = {total: toSave.length, succeeded: 0, failed: 0, failedFeatures: []};

    for (const {layerId, features: layerFeatures} of groupByLayer(toSave)) {
        // Featury vypuštěné frontou (přesunuly se znovu) se posílat nebudou – pro volajícího jsou vyřízené.
        let sent: any[] = [];
        const send = async (payload: any[]): Promise<void> => {
            sent = payload;
            const progressId = onProgress ? newProgressId() : undefined;
            // Featury z už hotových vrstev a z této vrstvy ty vypuštěné; k nim se přičítá průběh hlášený serverem.
            const settled = result.succeeded + result.failed + (layerFeatures.length - payload.length);
            const tracker = progressId ? trackBatch(progressId, done => onProgress({done: done == null ? null : settled + done, total: result.total})) : null;
            const save = (): Promise<unknown> => UserLayersApi.updateLayerFeaturesBatch(layerId, undefined, progressId, payload.map(toWrapper));
            try {
                const request = continueOnError ? save() : actionApiCall(save);
                await (tracker ? awaitBatch(request, tracker) : request);
            } finally {
                tracker?.stop();
            }
        };
        try {
            await enqueueLayerWrite(layerId, layerFeatures, send);
            result.succeeded += layerFeatures.length;
        } catch (error) {
            if (!continueOnError) {
                throw error;
            }
            // Transakce vrstvy se nezdařila → žádná z odeslaných featur není uložena, vrátíme je k rollbacku.
            result.failed += sent.length;
            result.failedFeatures.push(...sent);
            result.succeeded += layerFeatures.length - sent.length;
        }
        onProgress?.({done: result.succeeded + result.failed, total: result.total});
    }
    return result;
}

/**
 * Unikátní ID vrstev dotčených zadanými featurami (přednostně `parentLayerId`).
 * Používá se k obnově původní geometrie po zrušení/selhání přesunu (znovunačtením vrstev).
 */
export function affectedLayerIds(features: any[]): number[] {
    const ids = new Set<number>();
    for (const feature of features) {
        const layerId = feature.properties?.parentLayerId ?? feature.properties?.layerId;
        if (layerId != null) {
            ids.add(layerId);
        }
    }
    return [...ids];
}

/**
 * Zeptá se uživatele na potvrzení přesunu velkého množství objektů.
 * Vrací `true`, pokud se má v přesunu pokračovat (u malého počtu objektů rovnou `true` bez dotazu).
 */
export async function confirmBulkMove(count: number): Promise<boolean> {
    if (count < MOVE_CONFIRM_THRESHOLD) {
        return true;
    }
    const answer = await showConfirm({
        title: 'Přesun objektů',
        message: `Chystáte se přesunout velké množství objektů (${count}). Opravdu to chcete udělat?`,
        confirmText: 'Potvrdit',
        cancelText: 'Zrušit',
        iconIntent: 'warning',
    });
    return answer === true;
}

/**
 * Minimální „store" průběhu jedné notifikace. Toaster neumí obsah zobrazené notifikace přepsat, tak
 * do ní vložíme komponentu, která se sama překresluje – notifikace tím zůstává jedna a nebliká.
 */
function createProgressStore(initial: SaveProgress) {
    let value = initial;
    const listeners = new Set<() => void>();
    return {
        get: (): SaveProgress => value,
        set: (next: SaveProgress): void => {
            value = next;
            listeners.forEach(listener => listener());
        },
        subscribe: (listener: () => void): (() => void) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
    };
}

type ProgressStore = ReturnType<typeof createProgressStore>;

/** Text notifikace o průběhu ukládání – překresluje se, jak server hlásí zpracované featury. */
const SaveProgressMessage: React.FC<{store: ProgressStore}> = ({store}) => {
    const [progress, setProgress] = useState(store.get);
    useEffect(() => store.subscribe(() => setProgress(store.get())), [store]);
    return (
        <>
            {progress.done == null ? 'Ukládání stále probíhá.' : `Zpracováno ${progress.done} z ${progress.total} objektů.`} Nezavírejte ani nepřenačítejte
            stránku.
        </>
    );
};

/**
 * Počet přesunů, které se právě ukládají na pozadí. „Na pozadí" znamená v této záložce prohlížeče –
 * po zavření/přenačtení stránky nemá kdo ukládání dokončit, proto se nedokončený zápis hlídá.
 */
let pendingBackgroundSaves = 0;

/** Nechá prohlížeč zobrazit nativní varování „změny se nemusí uložit" při zavření/F5. */
const warnOnUnload = (event: BeforeUnloadEvent): void => {
    event.preventDefault();
    // Starší prohlížeče se řídí returnValue; vlastní text zobrazit nelze, hlášku určuje prohlížeč.
    event.returnValue = '';
};

function trackPendingSave(delta: 1 | -1): void {
    const wasPending = pendingBackgroundSaves > 0;
    pendingBackgroundSaves = Math.max(0, pendingBackgroundSaves + delta);
    const isPending = pendingBackgroundSaves > 0;
    if (isPending === wasPending) {
        return;
    }
    if (isPending) {
        globalThis.addEventListener('beforeunload', warnOnUnload);
    } else {
        globalThis.removeEventListener('beforeunload', warnOnUnload);
    }
}

/**
 * Optimisticky uloží přesunuté featury na pozadí: engine už objekty zobrazuje na novém místě,
 * ukládání běží asynchronně bez blokujícího dialogu – aplikace zůstává plně použitelná.
 * Volá se stylem „fire-and-forget".
 *
 * Po dobu ukládání je u hromadného přesunu vidět notifikace s průběhem („Zpracováno X z Y objektů"),
 * která se po dokončení požadavku sama zavře, a stránka je chráněná `beforeunload` varováním –
 * přenačtení či zavření záložky by rozdělané ukládání přerušilo, protože běží v prohlížeči,
 * ne jako serverová úloha.
 *
 * Featury, jejichž uložení selže, se přes `onRollback` vrátí na původní místo a uživatel dostane
 * jednu agregovanou chybu.
 */
export function persistMovedFeaturesInBackground(features: any[], options: {onRollback: (failedFeatures: any[]) => void}): void {
    const toSave = features.filter(isPersistableFeature);
    if (toSave.length === 0) {
        return;
    }
    // Jednotlivý objekt se uloží prakticky okamžitě – průběh hlásíme jen u hromadného přesunu.
    const store = toSave.length > 1 ? createProgressStore({done: 0, total: toSave.length}) : null;
    const progressToastKey = store ? toaster.infoWithTitle('Probíhá ukládání přesunu', <SaveProgressMessage store={store} />, {timeout: 0}) : null;
    trackPendingSave(1);
    void updateFeaturesBatched(features, {continueOnError: true, onProgress: store?.set})
        .then(result => {
            if (result.failed > 0) {
                options.onRollback(result.failedFeatures);
                toaster.errorWithTitle(
                    'Část přesunu se nezdařila',
                    `${result.failed} z ${result.total} objektů se nepodařilo uložit – byly vráceny na původní místo.`,
                );
                return;
            }
            // Jednotlivý objekt se přesune vizuálně okamžitě, potvrzujeme jen hromadný přesun.
            if (result.succeeded > 1) {
                toaster.success(`Přesun ${result.succeeded} objektů byl dokončen.`);
            }
        })
        .finally(() => {
            trackPendingSave(-1);
            if (progressToastKey) {
                toaster.close(progressToastKey);
            }
        });
}
